import { createContext, useState, useContext, useEffect } from 'react';

const AppContext = createContext(undefined);

export const AppProvider = ({ children }) => {
  const [userId, setUserId] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pasteCodeResult, setPasteCodeResult] = useState('');
  const [reward, setReward] = useState('');
  const [collectionIds, setCollectionIds] = useState([]);
  const [userProfile, setUserProfile] = useState(() => {
    const savedProfile = localStorage.getItem("userProfile");
    if (savedProfile) {
      return JSON.parse(savedProfile);
    }
    return {
      isLoggedIn: false,
      preferences: {},
      collections: [],
    };
  });
  
  const addCollectionId = (collectionId) => {
    setCollectionIds((prevCollectionIds) => [...prevCollectionIds, collectionId]);
  };

  const removeCollectionId = (index) => {
    setCollectionIds((prevCollectionIds) => prevCollectionIds.filter((_, i) => i !== index));
  };

  const updateProfile = (updates) => {
    setUserProfile((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  useEffect(() => {
    // Hydrate sensitive info from SDK at runtime
    if (window.nbc?.identity?.user) {
      setUserProfile(prev => ({
        ...prev,
        firstName: window.nbc.identity.user.givenName || '',
        lastName: window.nbc.identity.user.sn || '',
        userId: window.nbc.identity.user._id || ''
      }));
    }
    const savedProfile = localStorage.getItem("userProfile");
    if (savedProfile) {
      const { isLoggedIn, preferences, collections } = JSON.parse(savedProfile);
      setUserProfile(prev => ({
        ...prev,
        isLoggedIn: isLoggedIn || false,
        preferences: preferences || {},
        collections: collections || [],
      }));
    }

    const handleStorageChange = (event) => {
      if (event.key === "userProfile" && event.newValue) {
        console.log("Storage event detected in UserProfileContext");
        try {
          const newProfile = JSON.parse(event.newValue);
          console.log("New profile from storage event:", newProfile);
          setUserProfile(newProfile);
        } catch (error) {
          console.error("Error parsing userProfile from storage event:", error);
        }
      }
    };
  
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };

  }, []);

  useEffect(() => {

    const profileString = JSON.stringify(userProfile);
    if (profileString !== localStorage.getItem("userProfile")) {
      localStorage.setItem("userProfile", profileString);
      try {
        window.dispatchEvent(
          new StorageEvent("storage", {
            key: "userProfile",
            newValue: profileString,
            storageArea: localStorage,
          })
        );
      } catch (e) {
        console.error("Error dispatching storage event:", e);
      }
    }
  }, [userProfile]);

  const contextValue = {
    userProfile,
    updateProfile,
    userId, 
    setUserId, 
    isAuthenticated, 
    setIsAuthenticated, 
    pasteCodeResult, 
    setPasteCodeResult, 
    reward, 
    setReward, 
    collectionIds, 
    addCollectionId, 
    removeCollectionId
  }

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
}